<template>
    <div
        class="dk-one-step-lead-form-modal dst max-lg:tw-relative max-lg:tw-h-full lg:tw-h-full lg:tw-flex lg:tw-flex-col lg:tw-justify-between"
    >
        <div class="dk-one-step-lead-form-modal__content max-lg:tw-h-full max-lg:tw-overflow-y-auto">
            <div
                class="dk-one-step-lead-form-modal__header tw-w-full tw-px-6 tw-py-3 tw-bg-brand-primary tw-text-white"
            >
                <div
                    class="dk-one-step-lead-form-modal__title tw-full tw-flex tw-justify-between tw-items-center tw-mb-1"
                >
                    <h4 class="headline-sm lg:tw-font-semibold">
                        {{ $t('multimodel.want-to-know-more') }}
                    </h4>
                    <dk-button
                        class="!tw-text-white !tw-p-0"
                        variant="ghost"
                        type="button"
                        @click="hideModal('dk-one-step-lead-form-modal')"
                    >
                        <dk-icon name="ds-close-default" size="s" />
                    </dk-button>
                </div>
                <span
                    class="dk-one-step-lead-form-modal__subtitle tw-text-sm lg:tw-text-base tw-leading-sm lg:tw-leading-base"
                    v-html="subtitle"
                ></span>
            </div>

            <div
                v-if="loading"
                class="dk-one-step-lead-form-modal__loader tw-flex tw-h-[480px] lg:tw-h-full tw-items-center tw-justify-center"
            >
                <dk-icon name="spinner" size="s" :is-spinning="true" />
            </div>

            <div class="dk-one-step-lead-form-submission lg:tw-relative lg:tw-w-full tw-p-6">
                <div
                    class="dk-one-step-lead-form-submission__body lg:tw-relative lg:tw-h-[564px] tw-overflow-y-auto [&::-webkit-scrollbar]:max-lg:tw-hidden [&::-webkit-scrollbar]:lg:tw-bg-border-color [&::-webkit-scrollbar]:lg:tw-rounded-[10px] [&::-webkit-scrollbar]:lg:tw-w-2 [&::-webkit-scrollbar-thumb]:lg:tw-w-1 [&::-webkit-scrollbar-thumb]:lg:tw-bg-negative-subtle [&::-webkit-scrollbar-thumb]:hover:lg:tw-bg-slate-400 [&::-webkit-scrollbar-thumb]:active:lg:tw-bg-slate-600 [&::-webkit-scrollbar-thumb]:lg:tw-rounded-[10px] [&::-webkit-scrollbar-thumb]:lg:tw-border-2 [&::-webkit-scrollbar-thumb]:lg:tw-border-solid [&::-webkit-scrollbar-thumb]:lg:tw-border-transparent [&::-webkit-scrollbar-thumb]:lg:tw-bg-clip-content"
                    :style="{ height: isMobile ? '' : `${customHeight}px` }"
                >
                    <h6
                        v-if="!loading"
                        class="dk-one-step-lead-form-submission__title tw-text-2xl tw-leading-lg tw-font-semibold tw-text-center"
                    >
                        {{ $t('wizard-lead-form.insert-your-data') }}
                    </h6>
                    <lead-form-wrapper
                        ref="leadFormWrapper"
                        class="tw-mt-4 lg:tw-mr-4"
                        :form-id="formId"
                        :form-data="formData"
                        :selected-models="items"
                        is-wizard-lead-form
                        @mounted="onLeadFormMounted"
                        @submit="onLeadFormSubmit"
                    />
                </div>
                <div
                    class="dk-one-step-lead-form-submission__gradient max-lg:tw-hidden tw-absolute tw-bottom-6 tw-right-12 tw-h-[88px] lg:tw-h-14 tw-w-full tw-pointer-events-none tw-bg-gradient-to-t tw-from-white"
                ></div>
            </div>
        </div>
        <div
            class="dk-wizard-lead-form-modal__footer tw-w-full tw-py-4 tw-px-6 max-lg:tw-hidden tw-pb-6 tw-pt-0 tw-mt-4"
        >
            <dk-button
                class="dk-one-step-lead-form-modal__submit-button tw-w-full"
                variant="accent"
                type="button"
                :disabled="isButtonDisabled"
                @click="onDiscoverPrice"
            >
                {{ $t('forms.cta.quote') }}
            </dk-button>
        </div>
    </div>
</template>

<script>
/**
 * One-step lead form modal
 */
import { dkModalAware } from '@v4/common/components/mixins/modal';
import { EventBus } from '@v4/common/utils/event-bus';
import { createMultiple } from '@v4/services/api/lead-form';
import { navigateTo } from '@v4/http/request';
import isFunction from 'lodash/isFunction';

export default {
    name: 'DkOneStepLeadFormModal',
    components: {
        DkButton: () => ({
            component: import(/* webpackChunkName: "DkButton-vue" */ '@v4/common/components/atoms/DkButton.vue'),
        }),
        DkIcon: () => ({
            component: import(/* webpackChunkName: "DkIcon-vue" */ '@v4/common/components/atoms/DkIcon.vue'),
        }),
        LeadFormWrapper: () => ({
            component: import(
                /* webpackChunkName: "LeadFormWrapper-vue" */ '@v4/common/components/organisms/LeadFormWrapper/LeadFormWrapper'
            ),
        }),
    },
    mixins: [dkModalAware],
    props: {
        formData: {
            type: Object,
            default: () => {},
        },
        subtitle: {
            type: String,
            default: '',
        },
        formId: {
            type: String,
            default: '',
        },
        items: {
            type: Array,
            default: () => [],
        },
        isMobile: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: true,
            customHeight: null,
            isSubmitDisabled: true,
            isFormBusy: false,
        };
    },
    computed: {
        isButtonDisabled() {
            return this.isSubmitDisabled || this.isFormBusy;
        },
        isComparePage() {
            return this.formId === 'compare';
        },
    },
    mounted() {
        EventBus.$on('DkLeadForm:submit-disabled-change', this.updateSubmitDisabled);
        this.handleModalSizeOnDesktop();
    },
    beforeDestroy() {
        EventBus.$off('DkLeadForm:submit-disabled-change', this.updateSubmitDisabled);
    },
    methods: {
        onLeadFormMounted() {
            this.loading = false;
        },
        handleModalSizeOnDesktop() {
            const modalMaxSize = 800;
            const defaultScrollSize = 564;
            if (window.innerHeight < modalMaxSize) {
                this.customHeight = defaultScrollSize - (modalMaxSize - window.innerHeight);
            }
        },
        updateSubmitDisabled(value) {
            this.isSubmitDisabled = value;

            if (this.isFormBusy && !this.isSubmitDisabled) {
                this.isFormBusy = false;
            }
        },
        onDiscoverPrice(e) {
            EventBus.$emit('WizardLeadForm:submit', e);
            this.isFormBusy = true;
        },
        onLeadFormSubmit({ isValid, data }) {
            if (!isValid) {
                return;
            }

            const items = !this.isComparePage ? this.items : this.getItemsForComparePage();

            createMultiple(data, items)
                .then(({ data }) => {
                    if (data.data && data.data.thankYouPageUrl) {
                        return navigateTo(data.data.thankYouPageUrl);
                    }
                })
                .catch((error) => {
                    if (error.response && error.response.data.data && error.response.data.data.message) {
                        this.setLeadFormErrorMessage(error.response.data.data.message);
                    }
                })
                .finally(() => {
                    this.setLeadFormIsBusy(false);
                });
        },
        setLeadFormIsBusy(bool) {
            if (!this.$refs.leadFormWrapper || !isFunction(this.$refs.leadFormWrapper.setLeadFormIsBusy)) {
                return;
            }

            this.$refs.leadFormWrapper.setLeadFormIsBusy(bool);
        },
        setLeadFormErrorMessage(message) {
            if (!this.$refs.leadFormWrapper || !isFunction(this.$refs.leadFormWrapper.setLeadFormErrorMessage)) {
                return;
            }

            this.$refs.leadFormWrapper.setLeadFormErrorMessage(message);
        },
        getItemsForComparePage() {
            return this.items.map((vehicle) => {
                const offer = vehicle.offers.find((item) => item.selected) || {};

                return {
                    ...vehicle,
                    promotionId: offer.promotionId || null,
                    projectId: offer.projectId || null,
                    poId: offer.poId || null,
                };
            });
        },
    },
};
</script>
